'use client';

import { FC, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { useSelector } from 'react-redux';
import { fetchRemoteConfig, firebaseAnalytics, clearItidaCustomSignal } from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { Carousel } from '@/hotdesk-ui/enum/listing-workspace';
import { getDisplayLanguageCode } from '@/hotdesk-ui/lib/language.service';
import { useChangeLanguage } from '@/hotdesk-ui/hooks/useChangeLanguage';
import { Languages, LanguageCode } from '@/hotdesk-ui/constants/langulage.constant';
import { logout } from '@/hotdesk-ui/store/slices/auth.slice';
import { setIsOpen } from '@/hotdesk-ui/store/slices/popup.slice';
import { setGeoLocation, toggelLocationPopup } from '@/hotdesk-ui/store/slices/geo-location.slice';
import { getDefaultLocation } from '@/hotdesk-ui/helpers/location.helper';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { StorageKeys } from '@/hotdesk-ui/enum/storage-keys';
import { storeSet } from '@/hotdesk-ui/helpers/utils';
import { appendExploreLocationParams } from '@/hotdesk-ui/helpers/explore-location-params';
import { getWorkspaceData, getListingData, setFilters } from '@/hotdesk-ui/store/slices/listing.slice';
import { setCurrentSearch } from '@/hotdesk-ui/store/slices/search.slice';
import { AuthPopupService } from '@/hotdesk-ui/services/authPopup.service';
import { geoDebug } from '@/hotdesk-ui/helpers/debug-geo';
import { ReadOnlyListing } from 'hotdesk_typescript';
import { fetchFallbackCarousels } from '@/hotdesk-ui/store/slices/fallback-carousels.slice';
import ExpansionRequestBanner from '@/hotdesk-ui/components/expansion-request/ExpansionRequestBanner';
import { useWorkspaceTypeFilter } from '@/hotdesk-ui/hooks/useWorkspaceTypeFilter';
import { parseRatingFromMetaData } from '@/hotdesk-ui/helpers/rating.utils';
import { HeroSearch, CarouselSection, InstantBookingsBanner } from '@/hotdesk-ui/components/shared';
import styles from './LandingClient.module.scss';
interface LandingClientProps {
  languageCode: LanguageCode;
  isDirectory: boolean;
  ipCountryCode?: string;
  ipCity?: string;
  ipState?: string;
  ipLatitude?: number;
  ipLongitude?: number;
  fallbackCarouselsEnabled?: boolean;
}

// Carousel rows to shimmer while the landing data loads — enough to fill the
// viewport instead of stopping two rows in. Empty entries have no heading of
// their own yet (fallback carousels are titled by their data), so they get a
// shimmer heading from CarouselSection.
const CAROUSEL_SKELETON_TITLE_KEYS = ['popularNearYou', 'topRated', 'trending', '', '', ''] as const;

// Build a minimal GeoCodeResult from edge IP headers. Used to prime Redux
// before Google Maps loads (or when the persisted record is untrusted), so
// the search bar, workspace query, and hero title all agree on the same
// country immediately on mount.
const buildIpGeoCodeResult = (countryCode: string | undefined, city: string | undefined, state: string | undefined, lat: number, lng: number): GeoCodeResult => {
  const formatted_address = [city, state, countryCode].filter(Boolean).join(', ') || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  return {
    address_components: [] as unknown as GeoCodeResult['address_components'],
    formatted_address,
    geometry: {
      location: {
        lat,
        lng
      },
      location_type: 'APPROXIMATE'
    },
    place_id: '',
    plus_code: {
      compound_code: '',
      global_code: ''
    },
    types: [] as unknown as GeoCodeResult['types'],
    country: countryCode,
    country_sort: countryCode,
    city,
    state,
    lat,
    log: lng,
    source: 'ip-fallback'
  };
};
const LandingClient: FC<LandingClientProps> = ({
  languageCode,
  isDirectory,
  ipCountryCode,
  ipCity,
  ipState,
  ipLatitude,
  ipLongitude,
  fallbackCarouselsEnabled = false
}) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const popService = new AuthPopupService();
  const t = useTranslations('landing-v4.carousel');
  const user = useSelector((state: RootState) => state.auth.user);
  const workspacesSelector = useAppSelector((state: RootState) => state.listing.workspaces);
  const listingsSelector = useAppSelector((state: RootState) => state.listing.listings);
  const workspacesFetched = useAppSelector((state: RootState) => state.listing.workspacesFetched);
  const isWorkspaceLoading = useAppSelector((state: RootState) => state.listing.isWorkspaceLoading);
  const isListingLoading = useAppSelector((state: RootState) => state.listing.isListingLoading);
  const geoLocation = useAppSelector((state: RootState) => state.geoLocation.geoLocationZipData);
  const [language, setLanguage] = useState(`language.${languageCode}`);
  const [isRTL, setIsRTL] = useState(false);
  // The workspace-type pills moved into the global header, so the selection
  // is read from redux rather than owned here.
  const {
    selectedTypes
  } = useWorkspaceTypeFilter();
  const [termLink, setTermLink] = useState('');
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState('');
  const [helpLink, setHelpLink] = useState('');
  const [blogUrl, setBlogUrl] = useState('/blog');
  const [appQrcData, setAppQrcData] = useState('');
  const fallbackCarousels = useAppSelector((state: RootState) => state.fallbackCarousels.items);
  const autocompleteInput = useRef<HTMLInputElement>(null);
  // Set once the IP-priming effect has fetched cards on mount.
  const ipPrimedRef = useRef(false);
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const [finalOne, setFinalOne] = useState<GeoCodeResult>();
  const [, startTransition] = useTransition();

  // Selecting a filter while scrolled down flips every carousel to a pair of
  // skeletons; the document collapses, the browser clamps the scroll to the
  // now-short page (a flash of the footer), then it snaps back near the top
  // when results return. Freeze the stack's last non-loading height during
  // the refetch so the page never shrinks and the scroll position holds.
  const carouselStackRef = useRef<HTMLDivElement>(null);
  const lastStackHeight = useRef(0);
  const languageOptions = Languages.map(lang => ({
    value: lang.code,
    label: lang.displayText
  }));
  const hostUrl = 'https://host.hotdesk.com/';
  const oneURL = 'https://one.hotdesk.com';
  const handleLanguageChange = useChangeLanguage(setLanguage);

  // ─── Remote Config ────────────────────────────────────────────
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const termLinkText = await fetchRemoteConfig(RemoteConfigKeys.TERMS_AND_CONDITIONS_KEY, 'object');
        setTermLink((termLinkText as Record<string, string>)[languageCode]);
        const ppLink = await fetchRemoteConfig(RemoteConfigKeys.PRIVACY_KEY, 'object');
        setPrivacyPolicyLink((ppLink as Record<string, string>)[languageCode]);
        const help = await fetchRemoteConfig(RemoteConfigKeys.HELPCENTER, 'object');
        setHelpLink((help as Record<string, string>)[languageCode]);
        const qrLink = await fetchRemoteConfig(RemoteConfigKeys.QR_LINK, 'object');
        setAppQrcData(qrLink as string);
        const blogRoute = await fetchRemoteConfig(RemoteConfigKeys.WEB_BLOG_ROUTE, 'object');
        setBlogUrl(blogRoute as string);
      } catch (error) {
        console.error(error);
      }
    };
    loadConfig();
  }, [languageCode]);
  const workspaces = useMemo(() => {
    const isTypeFiltered = selectedTypes.length > 0;
    const src = isTypeFiltered ? listingsSelector : workspacesSelector;
    return (src || []) as ReadOnlyListing[];
  }, [workspacesSelector, listingsSelector, selectedTypes]);
  const reduxFilters = useAppSelector((state: RootState) => state.listing.filters);

  // The landing never applies a subscription filter. A previous explore
  // session can leave `userSubscriptionIdIn` in redux (it's persisted), which
  // would make the carousels fetch ONLY the subscription's workspaces and badge
  // them all as "Applied". Strip it on mount — and, since results are persisted
  // too, refetch clean so the carousels show all workspaces again.
  useEffect(() => {
    if (!reduxFilters?.userSubscriptionIdIn) return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
      userSubscriptionIdIn,
      ...rest
    } = reduxFilters;
    dispatch(setFilters(rest));
    if (geoLocation?.lat && geoLocation?.log) {
      dispatch(getWorkspaceData({
        lastSearch: geoLocation,
        page: 1,
        filters: rest
      }));
      dispatch(getListingData({
        lastSearch: geoLocation,
        page: 1,
        filters: rest
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The header pills persist their selection, so the landing can mount with a
  // type already applied (picked here last visit, or on explore). The initial
  // load only fetches workspaces, so pull the matching listings too — without
  // this the carousels would render whatever `listing.listings` was persisted.
  useEffect(() => {
    if (!reduxFilters?.typeIn) return;
    if (!geoLocation?.lat || !geoLocation?.log) return;
    dispatch(getListingData({
      lastSearch: geoLocation,
      page: 1,
      filters: {
        typeIn: reduxFilters.typeIn
      }
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setIsRTL(document.dir === 'rtl');
  }, [languageCode]);
  useEffect(() => {
    startTransition(async () => {
      setLanguage(await getDisplayLanguageCode());
    });
  }, []);

  // Fetch the per-city fallback carousels when the flag is on.
  useEffect(() => {
    if (fallbackCarouselsEnabled) {
      dispatch(fetchFallbackCarousels(languageCode));
    }
  }, [fallbackCarouselsEnabled, languageCode, dispatch]);

  // ─── Search / Geolocation ─────────────────────────────────────
  const setCurrentAddress = (address: string) => {
    if (autocompleteInput.current) autocompleteInput.current.value = address;
  };

  // Silently load location (on mount) — no popup
  const initLocation = () => {
    geoDebug('initLocation: start (silent)');
    if ('geolocation' in navigator) {
      navigator.permissions.query({
        name: 'geolocation'
      }).then(permissionStatus => {
        geoDebug('initLocation: permission state', permissionStatus.state);
        if (permissionStatus.state === 'granted') {
          navigator.geolocation.getCurrentPosition(({
            coords
          }) => {
            geoDebug('initLocation: got coords', {
              lat: coords.latitude,
              lng: coords.longitude
            });
            getDefaultLocation(coords.latitude, coords.longitude, (result: GeoCodeResult) => {
              geoDebug('initLocation: reverse-geocoded', {
                country_sort: result.country_sort,
                country: result.country,
                city: result.city
              });
              setCurrentAddress(result.formatted_address);
              result.lat = coords.latitude;
              result.log = coords.longitude;
              result.source = 'user-geolocation';
              dispatch(setGeoLocation(result));
              dispatch(setCurrentSearch(result));
              // Swap to the nearby cards without
              // blanking the IP-location ones first.
              dispatch(getWorkspaceData({
                lastSearch: result,
                page: 1,
                keepPreviousResults: true
              }));
              storeSet(StorageKeys.GEO_LOCATION_DATA, result);
            });
          });
        } else {
          geoDebug('initLocation: permission not granted, falling back to remote default');
          loadDefaultLocation();
        }
      });
    } else {
      geoDebug('initLocation: navigator.geolocation unavailable, falling back to remote default');
      loadDefaultLocation();
    }
  };

  // User clicks the location pin in the search bar — show the permission
  // popup when it hasn't been granted yet.
  const getPermission = () => {
    geoDebug('getPermission: user-triggered');
    const applyCoords = (coords: GeolocationCoordinates) => {
      geoDebug('getPermission: got coords', {
        lat: coords.latitude,
        lng: coords.longitude
      });
      getDefaultLocation(coords.latitude, coords.longitude, (result: GeoCodeResult) => {
        geoDebug('getPermission: reverse-geocoded', {
          country_sort: result.country_sort,
          country: result.country,
          city: result.city
        });
        setCurrentAddress(result.formatted_address);
        result.lat = coords.latitude;
        result.log = coords.longitude;
        result.source = 'user-geolocation';
        dispatch(setGeoLocation(result));
        dispatch(setCurrentSearch(result));
        dispatch(getWorkspaceData({
          lastSearch: result,
          page: 1
        }));
        storeSet(StorageKeys.GEO_LOCATION_DATA, result);
      });
    };
    if (!('geolocation' in navigator)) {
      geoDebug('getPermission: navigator.geolocation unavailable, opening location popup');
      dispatch(toggelLocationPopup(true));
      return;
    }
    navigator.permissions.query({
      name: 'geolocation'
    }).then(permissionStatus => {
      geoDebug('getPermission: permission state', permissionStatus.state);
      if (permissionStatus.state === 'granted') {
        navigator.geolocation.getCurrentPosition(({
          coords
        }) => applyCoords(coords));
      } else {
        geoDebug('getPermission: not granted, opening location popup');
        dispatch(toggelLocationPopup(true));
      }
    });
  };
  const loadDefaultLocation = async () => {
    // Prefer IP-derived coords from edge headers when available — they
    // give a per-user fallback that's much closer to where the visitor
    // actually is than the global Remote Config default. Only drop to
    // Remote Config when the edge didn't expose coords.
    const useIpCoords = typeof ipLatitude === 'number' && typeof ipLongitude === 'number';
    // The IP-priming effect already loaded cards for these exact coords
    // on mount; fetching again only blanked and reloaded the same cards.
    if (useIpCoords && ipPrimedRef.current) {
      geoDebug('loadDefaultLocation: IP location already loaded, skipping');
      return;
    }
    let lat: number;
    let lng: number;
    let source: GeoCodeResult['source'];
    if (useIpCoords) {
      lat = ipLatitude as number;
      lng = ipLongitude as number;
      source = 'ip-fallback';
      geoDebug('loadDefaultLocation: using IP coords', {
        lat,
        lng
      });
    } else {
      const defaultLocation = (await fetchRemoteConfig(RemoteConfigKeys.DEFAULT_LOCATION, 'object')) as {
        [key: string]: number;
      };
      lat = defaultLocation.latitude;
      lng = defaultLocation.longitude;
      source = 'remote-default';
      geoDebug('loadDefaultLocation: using remote config default', defaultLocation);
    }
    getDefaultLocation(lat, lng, (result: GeoCodeResult) => {
      geoDebug('loadDefaultLocation: reverse-geocoded', {
        source,
        country_sort: result.country_sort,
        country: result.country,
        city: result.city
      });
      result.source = source;
      setCurrentAddress(result.formatted_address);
      dispatch(setGeoLocation(result));
      dispatch(setCurrentSearch(result));
      dispatch(getWorkspaceData({
        lastSearch: result,
        page: 1,
        keepPreviousResults: true
      }));
      storeSet(StorageKeys.GEO_LOCATION_DATA, result);
    });
  };
  useEffect(() => {
    if (autocompleteInput.current && geoLocation?.formatted_address) {
      autocompleteInput.current.value = geoLocation.formatted_address;
    }
  }, [geoLocation?.formatted_address]);

  // Prime geoLocation from edge IP headers when the persisted record is
  // untrusted (stale 'remote-default' from a previous session, or absent).
  // Runs synchronously on mount, ahead of initLocation — so the search bar,
  // workspace query, and hero title agree on the same country without
  // waiting for the Maps geocoder to load or for getCurrentPosition to
  // resolve (which can silently never fire when permission is 'granted').
  useEffect(() => {
    if (typeof ipLatitude !== 'number' || typeof ipLongitude !== 'number') {
      return;
    }
    const persistedSource = geoLocation?.source;
    const isPersistedTrusted = persistedSource === 'user-geolocation' || persistedSource === 'autocomplete' || persistedSource === 'ip-fallback';
    if (isPersistedTrusted) return;
    const ipResult = buildIpGeoCodeResult(ipCountryCode, ipCity, ipState, ipLatitude, ipLongitude);
    geoDebug('LandingClient: priming geoLocation from IP', {
      country: ipResult.country_sort,
      city: ipResult.city,
      had_persisted: Boolean(geoLocation),
      persisted_source: persistedSource
    });
    setCurrentAddress(ipResult.formatted_address);
    dispatch(setGeoLocation(ipResult));
    dispatch(setCurrentSearch(ipResult));
    ipPrimedRef.current = true;
    // Cards from the previous visit (persisted) stay until these arrive.
    dispatch(getWorkspaceData({
      lastSearch: ipResult,
      page: 1,
      keepPreviousResults: true
    }));
    storeSet(StorageKeys.GEO_LOCATION_DATA, ipResult);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Place picked from the custom suggestions dropdown. Set the location, then
  // navigate to explore for that place — same as clicking the search button
  // (a dropdown pick shouldn't just update the home page in place).
  const handlePlaceSelect = useCallback((result: GeoCodeResult) => {
    // Google predictions carry no source; "Nearby" arrives already
    // marked 'user-geolocation' and must keep it (explore shows
    // "Nearby" for that source).
    result.source = result.source ?? 'autocomplete';
    dispatch(setGeoLocation(result));
    dispatch(setCurrentSearch(result));
    storeSet(StorageKeys.GEO_LOCATION_DATA, result);
    setFinalOne(result);
    const params = new URLSearchParams({
      lang: languageCode
    });
    if (selectedTypes.length > 0) {
      params.set('typeIn', selectedTypes.join(','));
      params.set('active', Carousel.listing);
    }
    appendExploreLocationParams(params, result);
    firebaseAnalytics('search_clicked');
    router.push(`/workspaces?${params}`);
  }, [dispatch, languageCode, selectedTypes, router]);
  useEffect(() => {
    if (finalOne) {
      storeSet(StorageKeys.GEO_LOCATION_DATA, finalOne);
    } else {
      initLocation();
    }
  }, []);

  // ─── Handlers ─────────────────────────────────────────────────
  const sentData = (typeOverride?: number) => {
    const params = new URLSearchParams({
      lang: languageCode
    });
    const overrideNum = typeof typeOverride === 'number' ? typeOverride : undefined;
    const types = overrideNum !== undefined ? [overrideNum].filter(Boolean) : selectedTypes;
    if (types.length > 0) {
      params.set('typeIn', types.join(','));
      params.set('active', Carousel.listing);
    }
    appendExploreLocationParams(params, finalOne || geoLocation);
    firebaseAnalytics('search_clicked');
    router.push(`/workspaces?${params}`);
  };
  const scrollToSection = async (sectionId: string, eventName: string) => {
    const doScroll = () => {
      document.querySelector(sectionId)?.scrollIntoView({
        behavior: 'smooth'
      });
    };
    if (pathname === '/') doScroll();else {
      await router.push('/');
      setTimeout(doScroll, 300);
    }
    firebaseAnalytics(eventName, {
      source: 'header'
    });
  };
  const moveToRoute = (eventName?: string) => {
    if (eventName) firebaseAnalytics(eventName, {
      source: 'header'
    });
  };
  const moveToRouteFooter = (eventName?: string) => {
    if (eventName) firebaseAnalytics(eventName, {
      source: 'footer'
    });
  };
  const signIn = async () => {
    const screen = pathname === '/' ? 'landing' : pathname === '/workspaces' ? 'explore' : pathname.startsWith('/workspaces') ? 'workspace' : pathname.replace('/', '');
    await firebaseAnalytics('web_auth_entry', {
      screen
    });
    popService.getPopup();
  };
  const signOut = async () => {
    dispatch(logout());
    await clearItidaCustomSignal();
    window.location.href = '/';
  };
  const osClick = () => {
    firebaseAnalytics('for_workspaces_clicked', {
      source: 'header'
    });
  };
  const osClickFooter = () => {
    firebaseAnalytics('for_workspaces_clicked', {
      source: 'footer'
    });
  };
  const oneClick = () => {
    firebaseAnalytics('one_clicked', {
      source: 'footer'
    });
  };
  const appQRCode = () => {
    firebaseAnalytics('download_app_footer', {
      source: 'footer'
    });
    if (window.innerWidth > 992) dispatch(setIsOpen(true));else window.location.href = appQrcData;
  };
  const carouselLoading = selectedTypes.length > 0 ? isListingLoading : isWorkspaceLoading;

  // Remember the stack's height whenever it's showing real content, so the
  // next loading render can pin that height (see `carouselStackRef` above).
  // No dep array: runs after every commit; the guard skips loading renders,
  // leaving the ref holding the last good height when a refetch begins.
  useEffect(() => {
    if (!carouselLoading && carouselStackRef.current) {
      lastStackHeight.current = carouselStackRef.current.offsetHeight;
    }
  });

  // ─── Carousel sections ────────────────────────────────────────
  const section1 = workspaces.slice(0, Math.min(8, workspaces.length));

  // Top-rated: pick workspaces with genuinely high ratings (≥ 3.0 stars / 60 on 100-point scale)
  // Rate each card by the same metaData ListingCard reads for its badge:
  // the listing's own rating once a type is picked (listing cards), else
  // the workspace's. Mixing them let an unrated listing in on its
  // workspace's rating and show up here without a badge.
  const TOP_RATED_MIN = 60;
  const isListingCards = selectedTypes.length > 0;
  const cardRating = (item: ReadOnlyListing) => parseRatingFromMetaData(isListingCards ? item.metaData : item.workspace?.metaData || item.metaData).overall;
  const section2 = [...workspaces].filter(item => cardRating(item) >= TOP_RATED_MIN).sort((a, b) => cardRating(b) - cardRating(a)).slice(0, 8);
  const section3 = workspaces.slice(16, Math.min(24, workspaces.length));
  const [countryFallbackLoaded, setCountryFallbackLoaded] = useState(false);

  // Country code: trust geolocation/autocomplete-sourced records, but treat
  // the remote-config fallback as untrusted for display — prefer the IP
  // country in that case so the title reflects where the user actually is.
  const countryCode = useMemo(() => {
    const fromGeo = geoLocation?.country_sort;
    const geoSource = geoLocation?.source;
    const geoIsTrusted = Boolean(fromGeo) && geoSource !== 'remote-default';
    const chosen = geoIsTrusted ? fromGeo : ipCountryCode || fromGeo;
    geoDebug('LandingClient: countryCode resolved', {
      fromGeo,
      fromIp: ipCountryCode,
      geoSource,
      geoIsTrusted,
      chosen,
      choice: geoIsTrusted ? 'geolocation' : ipCountryCode ? 'ip' : fromGeo ? 'remote-default-fallback' : 'none'
    });
    return chosen;
  }, [geoLocation?.country_sort, geoLocation?.source, ipCountryCode]);
  const state = useMemo(() => {
    const fromGeo = geoLocation?.state;
    const geoSource = geoLocation?.source;
    const geoIsTrusted = Boolean(fromGeo) && geoSource !== 'remote-default';
    const chosen = geoIsTrusted ? fromGeo : ipState || fromGeo;
    geoDebug('LandingClient: state resolved', {
      fromGeo,
      fromIp: ipState,
      geoSource,
      geoIsTrusted,
      chosen,
      choice: geoIsTrusted ? 'geolocation' : ipState ? 'ip' : fromGeo ? 'remote-default-fallback' : 'none'
    });
    return chosen;
  }, [geoLocation?.state, geoLocation?.source, ipState]);
  const country = useMemo(() => {
    const fromGeo = geoLocation?.country;
    const geoSource = geoLocation?.source;
    const geoIsTrusted = Boolean(fromGeo) && geoSource !== 'remote-default';
    const chosen = geoIsTrusted ? fromGeo : ipCountryCode || fromGeo;
    geoDebug('LandingClient: country resolved', {
      fromGeo,
      fromIp: ipCountryCode,
      geoSource,
      geoIsTrusted,
      chosen,
      choice: geoIsTrusted ? 'geolocation' : ipCountryCode ? 'ip' : fromGeo ? 'remote-default-fallback' : 'none'
    });
    return chosen;
  }, [geoLocation?.country, geoLocation?.source, ipCountryCode]);
  useEffect(() => {
    if (carouselLoading || workspaces.length > 0 || !country || countryFallbackLoaded) {
      return;
    }
    setCountryFallbackLoaded(true);
    dispatch(getWorkspaceData({
      page: 1,
      country: country
    }));
  }, [carouselLoading, workspaces.length, country, countryCode, geoLocation, dispatch, countryFallbackLoaded]);

  // Tells the splash screen (app/layout.tsx) the first real cards are on
  // screen, so phones see content instead of a blank page and skeletons.
  const contentReady = !carouselLoading && workspaces.length > 0;
  useEffect(() => {
    if (!contentReady) return;
    (window as Window & {
      __hdContentReady?: boolean;
    }).__hdContentReady = true;
    window.dispatchEvent(new Event('hd:content-ready'));
  }, [contentReady]);
  return <div style={{
    minHeight: '100vh',
    background: '#fff'
  }} data-splash-wait="" data-sentry-component="LandingClient" data-sentry-source-file="LandingClient.tsx">
            {/* Bottom-centred announcement pill. Its intro is held until the
                carousels have real content behind it — the skeleton covers the
                page on first paint, and an animation played under it is one
                nobody sees. `carouselLoading` alone won't do: it starts false
                and only turns true once a fetch is dispatched, so it reads as
                "ready" during the very skeleton we're waiting out.
                 `workspaces` is the list actually on screen — listings when a
                type pill is active, workspaces otherwise — so it is the one
                signal valid in both modes. `workspacesFetched` only ever
                tracks the workspace fetch, so on its own it leaves the pill
                waiting forever whenever a type is selected; it stays as the
                OR so a settled-but-empty result still arms the intro. */}
            <InstantBookingsBanner start={!carouselLoading && (workspaces.length > 0 || workspacesFetched)} data-sentry-element="InstantBookingsBanner" data-sentry-source-file="LandingClient.tsx" />
            <HeroSearch autocompleteInput={autocompleteInput as any} autocompleteContainer={autocompleteContainer as any} getPermission={getPermission} sentData={sentData} countryCode={countryCode} searchDisplayKey={geoLocation?.formatted_address} onSelect={handlePlaceSelect} data-sentry-element="HeroSearch" data-sentry-source-file="LandingClient.tsx" />

            {/* Country-expansion CTA — only once a fetch has actually come
                back empty for a location we resolved.
                 `carouselLoading` alone did not establish that: it starts false
                and only turns true when a fetch is dispatched, which on this
                page is the tail of permissions → GPS fix → reverse-geocode.
                Until that chain finished, "not loading" and "found nothing"
                looked identical, so the banner told the visitor we had not
                launched in their city while we were still working out which
                city that was. `workspacesFetched` flips only when a fetch
                settles, and is not persisted, so it cannot inherit a previous
                session's answer. */}
            {workspacesFetched && !carouselLoading && workspaces.length === 0 && countryCode && <ExpansionRequestBanner countryCode={countryCode} stateCode={state} />}

            <div ref={carouselStackRef} className={styles.carouselStack} style={carouselLoading && lastStackHeight.current > 0 ? {
      minHeight: lastStackHeight.current
    } : undefined}>
                {/* Skeletons only when there is nothing to show yet; a
                    refetch keeps the current cards until the new ones land. */}
                {carouselLoading && workspaces.length === 0 ? <>
                        {/* One row per carousel the loaded page can show. The
                            first three keep their real headings; the rest
                            shimmer theirs, since those titles are data. */}
                        {CAROUSEL_SKELETON_TITLE_KEYS.map((key, i) => <CarouselSection key={i} title={key ? t(key) : ''} listings={[]} isRTL={isRTL} cardLayout="minimal" loading />)}
                    </> : <>
                        {section1.length > 0 && <CarouselSection title={t('popularNearYou')} listings={section1} isRTL={isRTL} cardLayout="minimal"
        // First row on screen: its photos are the LCP.
        priorityCount={2} variant={selectedTypes.length > 0 ? 'listing' : 'workspace'} />}
                        {section2.length > 0 && <CarouselSection title={t('topRated')} listings={section2} isRTL={isRTL} cardLayout="minimal" variant={selectedTypes.length > 0 ? 'listing' : 'workspace'} />}
                        {section3.length > 0 && <CarouselSection title={t('trending')} listings={section3} isRTL={isRTL} cardLayout="minimal" variant={selectedTypes.length > 0 ? 'listing' : 'workspace'} />}
                        {workspaces.length > 0 && section2.length === 0 && <CarouselSection title={t('explore')} listings={workspaces} isRTL={isRTL} cardLayout="minimal" variant={selectedTypes.length > 0 ? 'listing' : 'workspace'} />}

                        {/* Per-city fallback carousels (Unleash-gated). Always
                            rendered when populated; appear after the location-
                            based carousels so local results stay primary. */}
                        {fallbackCarouselsEnabled && fallbackCarousels.map((bucket, idx) => <CarouselSection key={bucket.country} title={bucket.label} listings={bucket.listings} isRTL={isRTL} cardLayout="minimal" variant="workspace"
        // With no local results these rows are the
        // first on screen, so their photos are the LCP.
        priorityCount={workspaces.length === 0 && idx === fallbackCarousels.findIndex(b => b.listings.length > 0) ? 2 : 0} />)}
                    </>}
            </div>
        </div>;
};
export default LandingClient;